import React, { useState, useEffect } from 'react';
import ImageCropper from '../components/common/ImageCropper';
import GroupUserNavComponent from '../components/common/groupAndUsers';
import styled from 'styled-components';
import { motion } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { get, post, put } from '../utils/apiUtils';
import { logout } from "../services/authService";
import { useToast } from '../components/common/ToastProvider';

const Container = styled.div`
  padding: 2rem;
`;

const Header = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 2rem;
`;

const Title = styled.h1`
  font-size: 2.2rem;
  font-weight: 700;
  background: var(--gradient-primary);
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  font-family: 'Orbitron', sans-serif;
`;

const SettingsLayout = styled.div`
  display: grid;
  grid-template-columns: 280px 1fr;
  gap: 2rem;
`;

const SettingsMenu = styled.div`
  background: #ffffff;
  border: 1px solid rgba(0, 102, 255, 0.1);
  border-radius: 16px;
  padding: 1.5rem;
  height: fit-content;
  box-shadow: 0 5px 15px rgba(0, 102, 255, 0.05);
`;

const MenuItem = styled.div`
  padding: 1rem;
  border-radius: 8px;
  margin-bottom: 0.5rem;
  font-weight: 500;
  cursor: pointer;
  display: flex;
  align-items: center;
  gap: 0.8rem;
  color: ${props => props.active ? 'var(--primary)' : 'var(--text-secondary)'};
  background: ${props => props.active ? 'rgba(0, 102, 255, 0.05)' : 'transparent'};
  
  &:hover {
    background: rgba(0, 102, 255, 0.03);
  }
`;

const MenuIcon = styled.div`
  font-size: 1.2rem;
  color: ${props => props.active ? 'var(--primary)' : 'var(--text-secondary)'};
  font-family: 'Orbitron', sans-serif;
`;

const SettingsContent = styled.div`
  background: #ffffff;
  border: 1px solid rgba(0, 102, 255, 0.1);
  border-radius: 16px;
  padding: 2rem;
  box-shadow: 0 5px 15px rgba(0, 102, 255, 0.05);
`;

const SectionTitle = styled.h2`
  font-size: 1.5rem;
  font-weight: 600;
  margin-bottom: 1.5rem;
  color: var(--text-primary);
`;

const FormGroup = styled.div`
  margin-bottom: 1.5rem;
`;

const FormRow = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 1.5rem;
  margin-bottom: 1.5rem;
`;

const Label = styled.label`
  display: block;
  margin-bottom: 0.5rem;
  font-weight: 500;
  color: var(--text-primary);
`;

const Input = styled.input`
  width: 100%;
  padding: 0.8rem 1rem;
  background: #ffffff;
  border: 1px solid rgba(0, 102, 255, 0.1);
  border-radius: 8px;
  color: var(--text-primary);
  font-size: 0.9rem;
  
  &:focus {
    outline: none;
    border-color: var(--primary);
    box-shadow: 0 0 0 2px rgba(0, 102, 255, 0.1);
  }
`;

const Toggle = styled.div`
  position: relative;
  display: inline-block;
  width: 50px;
  height: 26px;
`;

const ToggleInput = styled.input`
  opacity: 0;
  width: 0;
  height: 0;
  
  &:checked + span {
    background: var(--gradient-primary);
  }
  
  &:checked + span:before {
    transform: translateX(24px);
  }
`;

const Slider = styled.span`
  position: absolute;
  cursor: pointer;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-color: rgba(255, 255, 255, 0.1);
  transition: 0.4s;
  border-radius: 34px;
  
  &:before {
    position: absolute;
    content: "";
    height: 18px;
    width: 18px;
    left: 4px;
    bottom: 4px;
    background-color: white;
    transition: 0.4s;
    border-radius: 50%;
  }
`;

const ToggleRow = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 1rem 0;
  border-bottom: 1px solid rgba(255, 255, 255, 0.05);
  
  &:last-child {
    border-bottom: none;
  }
`;

const ToggleLabel = styled.div`
  display: flex;
  flex-direction: column;
`;

const ToggleTitle = styled.div`
  font-weight: 500;
  margin-bottom: 0.3rem;
`;

const ToggleDescription = styled.div`
  font-size: 0.85rem;
  color: var(--text-secondary);
`;

const ColorOption = styled.div`
  width: 30px;
  height: 30px;
  border-radius: 50%;
  background: ${props => props.color};
  cursor: pointer;
  border: 2px solid ${props => props.selected ? 'white' : 'transparent'};
  transition: all 0.2s ease;
  
  &:hover {
    transform: scale(1.1);
  }
`;

const ColorOptions = styled.div`
  display: flex;
  gap: 1rem;
  margin-top: 1rem;
`;

const Select = styled.select`
  width: 100%;
  padding: 0.8rem 1rem;
  background: #ffffff;
  border: 1px solid rgba(0, 102, 255, 0.1);
  border-radius: 8px;
  color: var(--text-primary);
  font-size: 0.9rem;
  
  &:focus {
    outline: none;
    border-color: var(--primary);
  }
  
  option {
    background: #ffffff;
    color: var(--text-primary);
  }
`;
const Modal = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background: rgba(0, 0, 0, 0.5);
  display: flex;
  justify-content: center;
  align-items: center;
  z-index: 1000;
`;

const ModalContent = styled.div`
  background: #ffffff;
  border-radius: 12px;
  padding: 2rem;
  width: 70%;
  max-width: 90%;
  box-shadow: 0 5px 15px rgba(0, 0, 0, 0.3);
  height:95dvh;
  overflow-y:auto;
`;

const ModalHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 1rem;
`;

const CloseButton = styled.button`
  background: transparent;
  border: none;
  font-size: 1.5rem;
  cursor: pointer;
  color: var(--text-secondary);
`;
const CardGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(200px, 1fr));
  gap: 1rem;
  margin-bottom: 1.5rem;
`;
const SelectableCard = styled.div`
  border: 2px solid ${props => (props.selected ? 'var(--primary)' : 'transparent')};
  border-radius: 8px;
  padding: 1rem;
  cursor: pointer;
  text-align: center;
  transition: all 0.2s ease;

  &:hover {
    transform: scale(1.05);
    border-color: var(--primary-light);
  }
`;
const Divider = styled.div`
  height: 1px;
  background: rgba(0, 0, 0, 0.1);
  margin: 1.5rem 0;
`;
const FileUpload = styled.div`
  margin-top: 1rem;

  input {
    display: block;
    margin-top: 0.5rem;
  }
`;
const ButtonGroup = styled.div`
  display: flex;
  justify-content: flex-end;
  gap: 1rem;
  margin-top: 2rem;
`;

const Button = styled(motion.button)`
  padding: 0.8rem 1.5rem;
  background: ${props => props.primary ? 'var(--gradient-primary)' : '#ffffff'};
  border: 1px solid ${props => props.primary ? 'transparent' : 'rgba(0, 102, 255, 0.1)'};
  border-radius: 8px;
  color: ${props => props.primary ? 'white' : 'var(--text-primary)'};
  font-weight: 600;
  font-size: 0.9rem;
  cursor: pointer;
  
  &:hover {
    background: ${props => props.primary ? 'var(--gradient-primary)' : 'rgba(0, 102, 255, 0.03)'};
    border-color: ${props => props.primary ? 'transparent' : 'rgba(0, 102, 255, 0.2)'};
  }
`;

const CardSeparator = styled.div`
  height: 1px;
  background: rgba(0, 102, 255, 0.1);
  margin: 2rem 0;
`;

const ApiCard = styled.div`
  background: #ffffff;
  border: 1px solid rgba(0, 102, 255, 0.1);
  border-radius: 12px;
  padding: 1.2rem;
  margin-bottom: 1rem;
  border: 1px solid rgba(0, 102, 255, 0.05);
  
  &:hover {
    border-color: var(--primary-light);
  }
`;

const ApiCardHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 1rem;
`;

const ApiName = styled.div`
  font-weight: 600;
`;

const ApiStatus = styled.div`
  padding: 0.3rem 0.8rem;
  border-radius: 50px;
  font-size: 0.75rem;
  background: ${props => props.connected ? 'rgba(16, 185, 129, 0.2)' : 'rgba(239, 68, 68, 0.2)'};
  color: ${props => props.connected ? 'var(--success)' : 'var(--error)'};
`;

const ApiInfo = styled.div`
  font-size: 0.85rem;
  color: var(--text-secondary);
  margin-bottom: 1rem;
`;

const ApiActions = styled.div`
  display: flex;
  gap: 0.8rem;
`;
const ValidationList = styled.ul`
  list-style: none;
  padding: 0;
  margin: 0.5rem 0 0;
`;

const ValidationItem = styled.li`
  font-size: 0.85rem;
  color: ${(props) => (props.isValid ? 'green' : 'red')};
  display: flex;
  align-items: center;
  gap: 0.5rem;
`;



const BASE_URL = process.env.REACT_APP_API_BASE_URL;
const PERS_ENDPOINT = `${BASE_URL}/api/v1/persistence/users`;
const AUTH_ENDPOINT = `${BASE_URL}/api/v1/authentication`;
const Settings = () => {
  const navigate = useNavigate();
  const { user, setUser, setProfileUpdated } = useAuth();
  const [currentUser, setcurrentUser] = useState(null);
  const [uploadedImage, setUploadedImage] = useState(null);
  const [croppedImage, setCroppedImage] = useState(null);
  const [showProfileModal, setShowProfileModal] = useState(false);
  const [showChangePassModal, setChangePassModal] = useState(false);
  const [agentIcon, setAgentIcon] = useState(null);
  const [activeSection, setActiveSection] = useState('profile');
  const [primaryColor, setPrimaryColor] = useState('#6366f1');
  const [uploadedFileName, setUploadedFileName] = useState('');

  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confNewPassword, setConfNewPassword] = useState('');

  const { showToast } = useToast();

  const handleCropComplete = (croppedFile) => {
    console.log('Uploaded file name:', croppedFile);

  };
  const handleFileUpload = (fileName) => {
    // console.log('handleFileUpload called with:', fileName);
    setUploadedFileName(fileName);
    // console.log('uploadedFileName after setState:', fileName);
    updateProfile(true);
  };

  const updateProfile = async (profileUpdateFlag = false) => {
    if (profileUpdateFlag) {
      currentUser.profile_url = uploadedFileName;
    }
    try {
      const response = await put(`${PERS_ENDPOINT}`, currentUser, {
        withCredentials: true,
      });
      setcurrentUser(response);
      setProfileUpdated(true); // Notify profile update
    } catch (error) {
      console.error('Error updating profile:', error);
      navigate('/login');
    }
  };
  const updatePass = async () => {
    if (!currentPassword || !newPassword) {
      showToast({
        message:'Please fill in both fields.',
        type:'warning'
      })
      return;
    }
    try {
      const response = await post(`${AUTH_ENDPOINT}/change_pwd`, {
        password: currentPassword,
        new_pwd: newPassword,
      });

      // if (response?.success) {
      console.log(response)
        showToast({
        message:'Password updated successfully!',
        type:'success'
      })
      setChangePassModal(false);
      setCurrentPassword('');
      setNewPassword('');
      await logout();
      navigate('/login');
      // } else {
      //   alert(response?.message || 'Failed to update password.');
      // }
    } catch (error) {
      console.error('Error updating password:', error);
      showToast({
        message:'An error occurred while updating the password.',
        type: 'error'
      })
    }
  }
  useEffect(() => {
    const fetchUserData = async () => {
      let payload = {
        sort: {},
        filter: {
          "username": currentUser?.username
        }
      }
      if (!currentUser) {
        try {
          const response = await post(`${PERS_ENDPOINT}/fetch?query=null`,
            payload
          );
          setcurrentUser(response[0]);
          if (response && response[0].profile_url) {
            setUploadedFileName(response[0].profile_url);
          }
        } catch (error) {
          console.error('Error fetching user data:', error);
          navigate('/login');
        }
      }
    };
    fetchUserData();

  }, [currentUser, navigate, setcurrentUser]);
  const validateCrop = (crop) => {
    console.log(JSON.stringify(crop));
    return {
      x: crop?.x ?? 0,
      y: crop?.y ?? 0,
      width: crop?.width ?? 50,
      height: crop?.height ?? 50,
      aspect: crop?.aspect ?? 1,
    };
  };
  const colorOptions = [
    { color: '#6366f1', name: 'Purple' },
    { color: '#3b82f6', name: 'Blue' },
    { color: '#06b6d4', name: 'Cyan' },
    { color: '#10b981', name: 'Green' },
    { color: '#f59e0b', name: 'Amber' },
    { color: '#ef4444', name: 'Red' }
  ];

  const renderContent = () => {
    switch (activeSection) {
      case 'profile':
        return (
          <>
            <SectionTitle>Profile Settings</SectionTitle>
            <FormRow>
              <FormGroup>
                <Label>First Name</Label>
                <Input type="text" value={currentUser?.first_name || ''}
                  onChange={(e) => setcurrentUser({ ...currentUser, first_name: e.target.value })}
                />

              </FormGroup>
              <FormGroup>
                <Label>Last Name</Label>
                <Input type="text" value={currentUser?.last_name || ''}
                  onChange={(e) => setcurrentUser({ ...currentUser, last_name: e.target.value })} />
              </FormGroup>
            </FormRow>
            <FormRow>
              <FormGroup>
                <Label>Gender</Label>
                <Select
                  value={currentUser?.gender || ''}
                  onChange={(e) => setcurrentUser({ ...currentUser, gender: e.target.value })}
                >
                  <option value="">Select Gender</option>
                  <option value="M">Male</option>
                  <option value="F">Female</option>
                  <option value="Other">Other</option>
                </Select>

              </FormGroup>
              <FormGroup>
                <Label>Date of Birth</Label>
                <Input
                  type="date"
                  value={currentUser?.date_of_birth ? currentUser.date_of_birth.split('T')[0] : ''}
                  onChange={(e) => {
                    const selectedDate = e.target.value;
                    const formattedDate = `${selectedDate}T00:00:00Z`;
                    setcurrentUser({ ...currentUser, date_of_birth: formattedDate });
                  }}
                />
              </FormGroup>
            </FormRow>
            <FormRow>
              <FormGroup>
                <Label>Email Address</Label>
                <Input type="email" value={currentUser?.email || ''} onChange={(e) => setcurrentUser({ ...currentUser, email: e.target.value })} />
              </FormGroup>

              <FormGroup>
                <Label>Phone</Label>
                <Input type="text" value={currentUser?.phone_number || ''} onChange={(e) => setcurrentUser({ ...currentUser, phone_number: e.target.value })} />
              </FormGroup>
            </FormRow>

            <FormRow>
              <FormGroup>
                <Label>Address</Label>
                <Input type="text" value={currentUser?.address || ''} onChange={(e) => setcurrentUser({ ...currentUser, address: e.target.value })} />
              </FormGroup>

            </FormRow>
            <FormGroup>
              <Label>Profile Image</Label>
              <Button
                secondary
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
                onClick={() => setShowProfileModal(true)} // Open modal on click
              >
                Upload Profile Picture
              </Button>
              {showProfileModal && (
                <Modal>
                  <ModalContent>
                    <ModalHeader>
                      <h3>Upload Profile Picture</h3>
                      <CloseButton onClick={() => setShowProfileModal(false)}>✖</CloseButton>
                    </ModalHeader>
                    <Divider />
                    <ImageCropper
                      uploadedImage={uploadedImage}
                      onCropComplete={handleCropComplete}
                      onFileUpload={handleFileUpload}
                    />
                  </ModalContent>
                </Modal>
              )}
            </FormGroup>
            <ButtonGroup>


              <Button
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
              >
                Cancel
              </Button>
              <Button onClick={() => updateProfile()}
                primary
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
              >
                Save Changes
              </Button>
            </ButtonGroup>
          </>
        );

      case 'appearance':
        return (
          <>
            <SectionTitle>Appearance Settings</SectionTitle>
            <FormGroup>
              <Label>Theme Mode</Label>
              <Select>
                <option value="dark">Dark Mode</option>
                <option value="light">Light Mode</option>
                <option value="system">System Default</option>
              </Select>
            </FormGroup>
            <FormGroup>
              <Label>Primary Color</Label>
              <ColorOptions>
                {colorOptions.map(option => (
                  <ColorOption
                    key={option.color}
                    color={option.color}
                    selected={primaryColor === option.color}
                    onClick={() => setPrimaryColor(option.color)}
                  />
                ))}
              </ColorOptions>
            </FormGroup>
            <CardSeparator />
            <FormGroup>
              <Label>UI Density</Label>
              <Select>
                <option value="comfortable">Comfortable</option>
                <option value="compact">Compact</option>
                <option value="spacious">Spacious</option>
              </Select>
            </FormGroup>
            <FormGroup>
              <Label>Animation Settings</Label>
              <ToggleRow>
                <ToggleLabel>
                  <ToggleTitle>Enable UI Animations</ToggleTitle>
                  <ToggleDescription>Show animations and transitions in the interface</ToggleDescription>
                </ToggleLabel>
                <Toggle>
                  <ToggleInput type="checkbox" defaultChecked />
                  <Slider />
                </Toggle>
              </ToggleRow>
              <ToggleRow>
                <ToggleLabel>
                  <ToggleTitle>Reduced Motion</ToggleTitle>
                  <ToggleDescription>Minimize animations for accessibility</ToggleDescription>
                </ToggleLabel>
                <Toggle>
                  <ToggleInput type="checkbox" />
                  <Slider />
                </Toggle>
              </ToggleRow>
            </FormGroup>
            <ButtonGroup>
              <Button
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
              >
                Reset to Default
              </Button>
              <Button
                primary
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
              >
                Apply Changes
              </Button>
            </ButtonGroup>
          </>
        );

      case 'notifications':
        return (
          <>
            <SectionTitle>Notification Settings</SectionTitle>
            <FormGroup>
              <Label>Notification Preferences</Label>
              <ToggleRow>
                <ToggleLabel>
                  <ToggleTitle>Email Notifications</ToggleTitle>
                  <ToggleDescription>Receive important updates via email</ToggleDescription>
                </ToggleLabel>
                <Toggle>
                  <ToggleInput type="checkbox" defaultChecked />
                  <Slider />
                </Toggle>
              </ToggleRow>
              <ToggleRow>
                <ToggleLabel>
                  <ToggleTitle>SMS Notifications</ToggleTitle>
                  <ToggleDescription>Receive urgent alerts via SMS</ToggleDescription>
                </ToggleLabel>
                <Toggle>
                  <ToggleInput type="checkbox" defaultChecked />
                  <Slider />
                </Toggle>
              </ToggleRow>
              <ToggleRow>
                <ToggleLabel>
                  <ToggleTitle>In-App Notifications</ToggleTitle>
                  <ToggleDescription>Show notifications in the application</ToggleDescription>
                </ToggleLabel>
                <Toggle>
                  <ToggleInput type="checkbox" defaultChecked />
                  <Slider />
                </Toggle>
              </ToggleRow>
            </FormGroup>
            <CardSeparator />
            <FormGroup>
              <Label>Alert Types</Label>
              <ToggleRow>
                <ToggleLabel>
                  <ToggleTitle>Policy Updates</ToggleTitle>
                  <ToggleDescription>Changes to policy terms or coverage</ToggleDescription>
                </ToggleLabel>
                <Toggle>
                  <ToggleInput type="checkbox" defaultChecked />
                  <Slider />
                </Toggle>
              </ToggleRow>
              <ToggleRow>
                <ToggleLabel>
                  <ToggleTitle>Claim Status Changes</ToggleTitle>
                  <ToggleDescription>Updates when a claim status changes</ToggleDescription>
                </ToggleLabel>
                <Toggle>
                  <ToggleInput type="checkbox" defaultChecked />
                  <Slider />
                </Toggle>
              </ToggleRow>
              <ToggleRow>
                <ToggleLabel>
                  <ToggleTitle>AI-Generated Insights</ToggleTitle>
                  <ToggleDescription>Risk predictions and policy recommendations</ToggleDescription>
                </ToggleLabel>
                <Toggle>
                  <ToggleInput type="checkbox" defaultChecked />
                  <Slider />
                </Toggle>
              </ToggleRow>
              <ToggleRow>
                <ToggleLabel>
                  <ToggleTitle>System Announcements</ToggleTitle>
                  <ToggleDescription>Platform updates and maintenance notices</ToggleDescription>
                </ToggleLabel>
                <Toggle>
                  <ToggleInput type="checkbox" />
                  <Slider />
                </Toggle>
              </ToggleRow>
            </FormGroup>
            <ButtonGroup>
              <Button
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
              >
                Reset to Default
              </Button>
              <Button
                primary
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
              >
                Save Preferences
              </Button>
            </ButtonGroup>
          </>
        );

      case 'ai':
        return (
          <>
            <SectionTitle>AI Agent Settings</SectionTitle>
            <FormGroup>
              <Label>Agentic AI Configuration</Label>
              <ToggleRow>
                <ToggleLabel>
                  <ToggleTitle>Enable AI Assistant</ToggleTitle>
                  <ToggleDescription>Activate AI agent for insights and automation</ToggleDescription>
                </ToggleLabel>
                <Toggle>
                  <ToggleInput type="checkbox" defaultChecked />
                  <Slider />
                </Toggle>
              </ToggleRow>
              <ToggleRow>
                <ToggleLabel>
                  <ToggleTitle>Proactive Insights</ToggleTitle>
                  <ToggleDescription>Allow AI to suggest actions and improvements</ToggleDescription>
                </ToggleLabel>
                <Toggle>
                  <ToggleInput type="checkbox" defaultChecked />
                  <Slider />
                </Toggle>
              </ToggleRow>
              <ToggleRow>
                <ToggleLabel>
                  <ToggleTitle>Autonomous Decision Making</ToggleTitle>
                  <ToggleDescription>Permit AI to make certain decisions without approval</ToggleDescription>
                </ToggleLabel>
                <Toggle>
                  <ToggleInput type="checkbox" />
                  <Slider />
                </Toggle>
              </ToggleRow>
            </FormGroup>
            <CardSeparator />
            <FormGroup>
              <Label>Learning & Preferences</Label>
              <ToggleRow>
                <ToggleLabel>
                  <ToggleTitle>Data Collection</ToggleTitle>
                  <ToggleDescription>Allow AI to learn from your interactions</ToggleDescription>
                </ToggleLabel>
                <Toggle>
                  <ToggleInput type="checkbox" defaultChecked />
                  <Slider />
                </Toggle>
              </ToggleRow>
              <ToggleRow>
                <ToggleLabel>
                  <ToggleTitle>Save Preferences</ToggleTitle>
                  <ToggleDescription>Remember your interaction preferences</ToggleDescription>
                </ToggleLabel>
                <Toggle>
                  <ToggleInput type="checkbox" defaultChecked />
                  <Slider />
                </Toggle>
              </ToggleRow>
            </FormGroup>
            <CardSeparator />
            <FormGroup>
              <Label>AI Performance Metrics</Label>
              <ToggleRow>
                <ToggleLabel>
                  <ToggleTitle>Track AI Effectiveness</ToggleTitle>
                  <ToggleDescription>Monitor and report on AI decision quality</ToggleDescription>
                </ToggleLabel>
                <Toggle>
                  <ToggleInput type="checkbox" defaultChecked />
                  <Slider />
                </Toggle>
              </ToggleRow>
              <ToggleRow>
                <ToggleLabel>
                  <ToggleTitle>Send Feedback Data</ToggleTitle>
                  <ToggleDescription>Help improve AI with anonymized feedback</ToggleDescription>
                </ToggleLabel>
                <Toggle>
                  <ToggleInput type="checkbox" defaultChecked />
                  <Slider />
                </Toggle>
              </ToggleRow>
            </FormGroup>
            <ButtonGroup>
              <Button
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
              >
                Reset AI Model
              </Button>
              <Button
                primary
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
              >
                Save AI Settings
              </Button>
            </ButtonGroup>
          </>
        );

      case 'integrations':
        return (
          <>
            <SectionTitle>External Integrations</SectionTitle>
            <FormGroup>
              <Label>Connected APIs</Label>
              <ApiCard>
                <ApiCardHeader>
                  <ApiName>Insurance Claims API</ApiName>
                  <ApiStatus connected>Connected</ApiStatus>
                </ApiCardHeader>
                <ApiInfo>
                  Connected since 05/12/2023. Last sync: 2 hours ago.
                </ApiInfo>
                <ApiActions>
                  <Button>
                    Configure
                  </Button>
                  <Button>
                    Disconnect
                  </Button>
                </ApiActions>
              </ApiCard>
              <ApiCard>
                <ApiCardHeader>
                  <ApiName>Risk Assessment API</ApiName>
                  <ApiStatus connected>Connected</ApiStatus>
                </ApiCardHeader>
                <ApiInfo>
                  Connected since 03/24/2023. Last sync: 4 hours ago.
                </ApiInfo>
                <ApiActions>
                  <Button>
                    Configure
                  </Button>
                  <Button>
                    Disconnect
                  </Button>
                </ApiActions>
              </ApiCard>
              <ApiCard>
                <ApiCardHeader>
                  <ApiName>Weather Data API</ApiName>
                  <ApiStatus connected={false}>Disconnected</ApiStatus>
                </ApiCardHeader>
                <ApiInfo>
                  API key expired on 02/15/2023.
                </ApiInfo>
                <ApiActions>
                  <Button primary>
                    Connect
                  </Button>
                </ApiActions>
              </ApiCard>
            </FormGroup>
            <CardSeparator />
            <FormGroup>
              <Label>Available Integrations</Label>
              <ApiCard>
                <ApiCardHeader>
                  <ApiName>CRM System Integration</ApiName>
                  <ApiStatus connected={false}>Not Connected</ApiStatus>
                </ApiCardHeader>
                <ApiInfo>
                  Connect your CRM system to synchronize customer data.
                </ApiInfo>
                <ApiActions>
                  <Button primary>
                    Connect
                  </Button>
                </ApiActions>
              </ApiCard>
              <ApiCard>
                <ApiCardHeader>
                  <ApiName>Banking API</ApiName>
                  <ApiStatus connected={false}>Not Connected</ApiStatus>
                </ApiCardHeader>
                <ApiInfo>
                  Integrate with banking systems for payment processing.
                </ApiInfo>
                <ApiActions>
                  <Button primary>
                    Connect
                  </Button>
                </ApiActions>
              </ApiCard>
            </FormGroup>
            <ButtonGroup>
              <Button
                primary
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
              >
                Add New Integration
              </Button>
            </ButtonGroup>
          </>
        );

      case 'security':
        return (
          <>
            <SectionTitle>Security Settings</SectionTitle>
            <FormGroup>
              <Label>Password & Authentication</Label>
              <FormRow>
                <FormGroup>
                  <Label>Current Password</Label>
                  <Input
                    type="password"
                    value={currentPassword}
                    onChange={(e) => setCurrentPassword(e.target.value)}
                    placeholder="Enter current password"
                  />
                </FormGroup>
              </FormRow>
              <FormRow>
                <FormGroup>
                  <Label>New Password</Label>
                  <Input
                    type="password"
                    value={newPassword}
                    onChange={(e) => setNewPassword(e.target.value)}
                    placeholder="Enter new password"
                  />
                  {newPassword.length >= 1 &&(
                  <ValidationList>
                    <ValidationItem isValid={newPassword.length >= 8 && newPassword.length <= 12}>
                      {newPassword.length >= 8 && newPassword.length <= 12 ? '✔' : '✖'} 8-12 Characters
                    </ValidationItem>
                    <ValidationItem isValid={/[A-Z]/.test(newPassword)}>
                      {/[A-Z]/.test(newPassword) ? '✔' : '✖'} At least one uppercase letter
                    </ValidationItem>
                    <ValidationItem isValid={/[!@#$%^&*(),.?":{}|<>]/.test(newPassword)}>
                      {/[!@#$%^&*(),.?":{}|<>]/.test(newPassword) ? '✔' : '✖'} At least one special character
                    </ValidationItem>
                  </ValidationList>
                  )}
                </FormGroup>
                <FormGroup>
                  <Label>Confirm New Password</Label>
                  <Input
                    type="password"
                    value={confNewPassword}
                    onChange={(e) => setConfNewPassword(e.target.value)}
                    placeholder="Confirm new password"
                  />
                  {confNewPassword.length >= 1 &&(
                  <ValidationList>
                    <ValidationItem isValid={confNewPassword === newPassword}>
                      {confNewPassword === newPassword ? '✔' : '✖'} Passwords match
                    </ValidationItem>
                  </ValidationList>
                )}
                </FormGroup>
              </FormRow>
              <ButtonGroup>
                <Button onClick={() => updatePass()}
                  primary
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.95 }}
                >
                  Change Password
                </Button>
              </ButtonGroup>
            </FormGroup>
            <CardSeparator />
            <FormGroup>
              <Label>Two-Factor Authentication</Label>
              <ToggleRow>
                <ToggleLabel>
                  <ToggleTitle>Enable 2FA</ToggleTitle>
                  <ToggleDescription>Require a verification code in addition to password</ToggleDescription>
                </ToggleLabel>
                <Toggle>
                  <ToggleInput type="checkbox" defaultChecked />
                  <Slider />
                </Toggle>
              </ToggleRow>
              <ToggleRow>
                <ToggleLabel>
                  <ToggleTitle>App Verification</ToggleTitle>
                  <ToggleDescription>Use an authenticator app for verification codes</ToggleDescription>
                </ToggleLabel>
                <Toggle>
                  <ToggleInput type="checkbox" defaultChecked />
                  <Slider />
                </Toggle>
              </ToggleRow>
              <ToggleRow>
                <ToggleLabel>
                  <ToggleTitle>SMS Verification</ToggleTitle>
                  <ToggleDescription>Receive verification codes via SMS</ToggleDescription>
                </ToggleLabel>
                <Toggle>
                  <ToggleInput type="checkbox" />
                  <Slider />
                </Toggle>
              </ToggleRow>
            </FormGroup>
            <CardSeparator />
            <FormGroup>
              <Label>Session Management</Label>
              <ToggleRow>
                <ToggleLabel>
                  <ToggleTitle>Auto Logout</ToggleTitle>
                  <ToggleDescription>Automatically log out after period of inactivity</ToggleDescription>
                </ToggleLabel>
                <Toggle>
                  <ToggleInput type="checkbox" defaultChecked />
                  <Slider />
                </Toggle>
              </ToggleRow>
              <FormRow>
                <FormGroup>
                  <Label>Inactivity Timeout</Label>
                  <Select>
                    <option value="15">15 minutes</option>
                    <option value="30">30 minutes</option>
                    <option value="60">1 hour</option>
                    <option value="120">2 hours</option>
                  </Select>
                </FormGroup>
              </FormRow>
            </FormGroup>
            <ButtonGroup>
              <Button
                primary
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
              >
                Save Security Settings
              </Button>
            </ButtonGroup>
          </>
        );

      default:
        return null;
    }
  };

  return (
    <Container>
      <Header>
        <Title>Settings</Title>
      </Header>

      <SettingsLayout>
        <SettingsMenu>
          <MenuItem
            active={activeSection === 'profile'}
            onClick={() => setActiveSection('profile')}
          >
            <MenuIcon>👤</MenuIcon>
            Profile
          </MenuItem>
          <MenuItem
            active={activeSection === 'appearance'}
            onClick={() => setActiveSection('appearance')}
          >
            <MenuIcon>🎨</MenuIcon>
            Appearance
          </MenuItem>
          {/* <MenuItem
            active={activeSection === 'notifications'}
            onClick={() => setActiveSection('notifications')}
          >
            <MenuIcon>🔔</MenuIcon>
            Notifications
          </MenuItem>
          <MenuItem
            active={activeSection === 'ai'}
            onClick={() => setActiveSection('ai')}
          >
            <MenuIcon>🤖</MenuIcon>
            AI Agent
          </MenuItem>
          <MenuItem
            active={activeSection === 'integrations'}
            onClick={() => setActiveSection('integrations')}
          >
            <MenuIcon>🔌</MenuIcon>
            Integrations
          </MenuItem> */}
          <MenuItem
            active={activeSection === 'security'}
            onClick={() => setActiveSection('security')}
          >
            <MenuIcon>🔒</MenuIcon>
            Security
          </MenuItem>

        </SettingsMenu>

        <SettingsContent
          key={activeSection}
        >
          {renderContent()}
        </SettingsContent>
      </SettingsLayout>
    </Container>
  );
};

export default Settings;
